/**
 * Per-commit cost receipt.
 *
 * When the agent runs `git commit` through the bash tool, a trailer like
 *
 *   Elyra-Cost: $0.42 (glm-5.3, 4 turns, 1 model switch)
 *
 * is appended to the commit message via `--trailer`, so spend per change is
 * visible in `git log`. This module is pure: it summarizes a window of agent
 * messages, formats the trailer, and rewrites shell commands. Session wiring
 * lives in agent-session.ts.
 */

import type { AgentMessage } from "@elyracode/agent-core";
import type { AssistantMessage } from "@elyracode/ai";

export const COST_TRAILER_KEY = "Elyra-Cost";

export interface CostWindowSummary {
	/** Total USD spent by assistant messages in the window (including errored attempts). */
	costUsd: number;
	/** Assistant messages in the window whose stopReason is not "error". */
	turns: number;
	/** Distinct model ids used in the window, in order of first appearance. */
	models: string[];
	/**
	 * Number of times consecutive assistant messages in the window used a different
	 * (provider, model) pair. A proxy for failovers and manual model changes; there is
	 * no session-scoped failover counter, so this is labeled "model switch" not "failover".
	 */
	switches: number;
}

/**
 * Summarize assistant spend for messages with `timestamp >= sinceTs`.
 * Errored messages contribute cost and count toward model switches (a failover
 * leaves an errored message behind) but are not counted as turns.
 */
export function summarizeCostWindow(messages: readonly AgentMessage[], sinceTs: number): CostWindowSummary {
	let costUsd = 0;
	let turns = 0;
	let switches = 0;
	const models: string[] = [];
	let previousKey: string | undefined;

	for (const message of messages) {
		if (message.role !== "assistant") continue;
		const assistant = message as AssistantMessage;
		if (assistant.timestamp < sinceTs) continue;

		costUsd += assistant.usage?.cost?.total ?? 0;
		if (assistant.stopReason !== "error") turns++;

		const key = `${assistant.provider}/${assistant.model}`;
		if (previousKey !== undefined && key !== previousKey) switches++;
		previousKey = key;
		if (!models.includes(assistant.model)) models.push(assistant.model);
	}

	return { costUsd, turns, models, switches };
}

function formatUsd(costUsd: number): string {
	if (costUsd > 0 && costUsd < 0.01) return "$<0.01";
	return `$${costUsd.toFixed(2)}`;
}

function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
	return `${count} ${count === 1 ? singular : pluralForm}`;
}

/** Format the trailer line. Zero-valued detail parts are omitted. */
export function formatCostTrailer(summary: CostWindowSummary): string {
	const parts: string[] = [];
	if (summary.models.length > 0) {
		const shown = summary.models.slice(0, 2).join(", ");
		const rest = summary.models.length - 2;
		parts.push(rest > 0 ? `${shown} +${rest} more` : shown);
	}
	if (summary.turns > 0) parts.push(plural(summary.turns, "turn"));
	if (summary.switches > 0) parts.push(plural(summary.switches, "model switch", "model switches"));

	const detail = parts.length > 0 ? ` (${parts.join(", ")})` : "";
	return `${COST_TRAILER_KEY}: ${formatUsd(summary.costUsd)}${detail}`;
}

// ── Shell command parsing ────────────────────────────────────────────────────

interface ShellWord {
	/** Unquoted value (quotes and backslash escapes removed). */
	value: string;
	start: number;
	end: number;
}

interface ShellSegment {
	words: ShellWord[];
}

function isSeparatorAt(command: string, i: number): boolean {
	const c = command[i];
	if (c === "\n" || c === ";" || c === "|") return true;
	return c === "&" && command[i + 1] === "&";
}

/**
 * Split a shell command into simple-command segments (separated by newline, `;`,
 * `|`, `||`, `&&`) and tokenize each into words, respecting single quotes, double
 * quotes, backslash escapes, and `$(...)` inside double quotes (so heredoc-style
 * `-m "$(cat <<'EOF' ... EOF)"` messages stay a single word).
 */
function parseSegments(command: string): ShellSegment[] {
	const segments: ShellSegment[] = [];
	let words: ShellWord[] = [];
	const n = command.length;
	let i = 0;

	const flush = () => {
		segments.push({ words });
		words = [];
	};

	while (i < n) {
		const ch = command[i];
		if (ch === " " || ch === "\t" || ch === "\r") {
			i++;
			continue;
		}
		if (isSeparatorAt(command, i)) {
			flush();
			i += (ch === "&" || ch === "|") && command[i + 1] === ch ? 2 : 1;
			continue;
		}

		const start = i;
		let value = "";
		let quote: '"' | "'" | undefined;
		let parenDepth = 0;
		while (i < n) {
			const c = command[i];
			if (quote === "'") {
				if (c === "'") quote = undefined;
				else value += c;
				i++;
				continue;
			}
			if (quote === '"') {
				if (parenDepth > 0) {
					if (c === "(") parenDepth++;
					else if (c === ")") parenDepth--;
					value += c;
					i++;
					continue;
				}
				if (c === "\\" && i + 1 < n) {
					value += command[i + 1];
					i += 2;
					continue;
				}
				if (c === "$" && command[i + 1] === "(") {
					parenDepth = 1;
					value += "$(";
					i += 2;
					continue;
				}
				if (c === '"') {
					quote = undefined;
					i++;
					continue;
				}
				value += c;
				i++;
				continue;
			}
			if (c === "\\" && i + 1 < n) {
				value += command[i + 1];
				i += 2;
				continue;
			}
			if (c === "'" || c === '"') {
				quote = c;
				i++;
				continue;
			}
			if (c === " " || c === "\t" || c === "\r" || isSeparatorAt(command, i)) break;
			value += c;
			i++;
		}
		words.push({ value, start, end: i });
	}
	flush();
	return segments;
}

const ENV_ASSIGNMENT = /^[A-Za-z_][A-Za-z0-9_]*=/;
/** Global git options that consume the following word. */
const GIT_OPTIONS_WITH_ARG = new Set(["-c", "-C", "--git-dir", "--work-tree", "--namespace", "--exec-path"]);

interface CommitInvocation {
	words: ShellWord[];
	/** Index of the `commit` word. */
	commitIndex: number;
	/** Index of a bare `--` pathspec separator after `commit`, if any. */
	dashDashIndex: number | undefined;
	/**
	 * True for commits that must not get a trailer: `--fixup`/`--squash` (messages
	 * are consumed by rebase) and `--amend` (rewrites a commit that already carries
	 * its receipt - a second trailer would stack, not replace).
	 */
	skip: boolean;
}

function findCommitInvocation(segment: ShellSegment): CommitInvocation | undefined {
	const { words } = segment;
	let j = 0;
	while (j < words.length && ENV_ASSIGNMENT.test(words[j].value)) j++;
	if (words[j]?.value !== "git") return undefined;
	j++;
	while (j < words.length && words[j].value.startsWith("-")) {
		j += GIT_OPTIONS_WITH_ARG.has(words[j].value) ? 2 : 1;
	}
	if (words[j]?.value !== "commit") return undefined;

	const commitIndex = j;
	let dashDashIndex: number | undefined;
	let skip = false;
	for (let k = commitIndex + 1; k < words.length; k++) {
		const value = words[k].value;
		if (value === "--") {
			dashDashIndex = k;
			break;
		}
		if (
			value === "--fixup" ||
			value.startsWith("--fixup=") ||
			value === "--squash" ||
			value.startsWith("--squash=") ||
			value === "--amend"
		) {
			skip = true;
		}
	}
	return { words, commitIndex, dashDashIndex, skip };
}

function findTrailerTargets(command: string): CommitInvocation[] {
	const targets: CommitInvocation[] = [];
	for (const segment of parseSegments(command)) {
		const invocation = findCommitInvocation(segment);
		if (invocation && !invocation.skip) targets.push(invocation);
	}
	return targets;
}

/**
 * True when the command contains at least one `git commit` invocation that should
 * receive a cost trailer. Handles leading env assignments, `&&`/`;`/`|` chains, and
 * git global options (`git -c x=y commit`). False for `--fixup`/`--squash`/`--amend`
 * commits and when the command already carries an `Elyra-Cost:` trailer.
 */
export function isGitCommitCommand(command: string): boolean {
	if (command.includes(`${COST_TRAILER_KEY}:`)) return false;
	return findTrailerTargets(command).length > 0;
}

function shellSingleQuote(value: string): string {
	return `'${value.replace(/'/g, `'\\''`)}'`;
}

/**
 * Append `--trailer '<trailer>'` to every eligible `git commit` invocation in the
 * command. The argument is inserted before a `--` pathspec separator when present,
 * otherwise after the last word of the invocation, so `-m "..."` arguments and
 * following chained commands are left intact.
 */
export function injectCostTrailer(command: string, trailer: string): string {
	const targets = findTrailerTargets(command);
	if (targets.length === 0) return command;

	const quoted = `--trailer ${shellSingleQuote(trailer)}`;
	const insertions: Array<{ at: number; text: string }> = targets.map((t) => {
		if (t.dashDashIndex !== undefined) {
			return { at: t.words[t.dashDashIndex].start, text: `${quoted} ` };
		}
		return { at: t.words[t.words.length - 1].end, text: ` ${quoted}` };
	});

	insertions.sort((a, b) => b.at - a.at);
	let result = command;
	for (const { at, text } of insertions) {
		result = `${result.slice(0, at)}${text}${result.slice(at)}`;
	}
	return result;
}
